import React from 'react';
import moment from 'moment-jalaali';
import { toPersianDigits, formatPrice, getStatusTitleById } from '../../../Buyer/OrdersPage/utils';

// تابع کمکی برای تبدیل مطمئن مقادیر به عدد
const toNumber = (val, def = 0) => {
    const n = Number(val);
    return Number.isFinite(n) ? n : def;
};

// فرمت شماره کارت بانکی (۴ رقم ۴ رقم با خط تیره)
const formatCardNumber = (cardNo) => {
    if (!cardNo) return '-';
    const cleaned = String(cardNo).replace(/\D/g, '');
    return toPersianDigits(cleaned.replace(/(\d{4})(?=\d)/g, '$1 - '));
};

export default function BuyerInvoicePrintContent({ invoiceData: rawInputData, contentRef }) {
    if (!rawInputData) return null;

    // ۱. نرمال‌سازی داده‌ها (پشتیبانی از { data: [...] } یا { data: {...} } یا خود {...})
    const invoiceData = Array.isArray(rawInputData?.data)
        ? rawInputData.data[0]
        : (rawInputData?.data || rawInputData);

    if (!invoiceData || typeof invoiceData !== 'object') return null;

    // ۲. استخراج اقلام، خلاصه‌ها و مقادیر کلی
    const items = Array.isArray(invoiceData.invoice_details || invoiceData.items)
        ? (invoiceData.invoice_details || invoiceData.items)
        : [];
    const rawSummary = invoiceData.summary || {};
    const rawTotals = invoiceData.totals || {};

    // ۳. محاسبه دقیق هزینه ارسال و بسته‌بندی
    const sendCost = toNumber(invoiceData.send_cost_price ?? rawSummary.sendCost ?? 0);
    const calculatedPack = items.reduce((acc, it) => acc + toNumber(it?.pack_cost), 0);
    const totalPackCost = toNumber(rawSummary.totalPackCost ?? calculatedPack);
    const totalShippingAndPack = toNumber(rawSummary.totalShippingAndPack ?? rawTotals.shipping ?? (sendCost + totalPackCost));

    // ۴. محاسبه مقادیر صورتحساب نهایی
    const calculatedConsumer = items.reduce((s, it) => s + toNumber(it?.totalPrice ?? it?.total ?? it?.price), 0);
    const summary = {
        totalConsumer: toNumber(rawSummary.totalConsumer ?? rawTotals.consumer ?? calculatedConsumer),
        totalProfit: toNumber(rawSummary.totalProfit ?? rawTotals.profit ?? 0),
        totalVat: toNumber(rawSummary.totalVat ?? rawTotals.vat ?? items.reduce((s, it) => s + toNumber(it?.vat_price), 0)),
        totalShippingAndPack,
        finalPayment: toNumber(rawSummary.finalPayment ?? invoiceData.total_price_invoice_archive ?? invoiceData.price ?? 0)
    };

    // ۵. مشخصات فروشنده و خریدار
    const seller = invoiceData.seller || { name: invoiceData.business?.title || invoiceData.shop || 'فروشگاه' };
    const buyer = invoiceData.buyer || {
        name: invoiceData.name_buyer_archive || invoiceData.buyer?.name || 'مشتری',
        family: invoiceData.family_buyer_archive || invoiceData.buyer?.family || ''
    };

    // ۶. وضعیت واترماک فاکتور
    const getStatusWatermark = () => {
        if (invoiceData.status === 'cancelled' || invoiceData.isRepealed || invoiceData.is_repeal) {
            return { text: 'ابطال شده', className: 'wm-cancelled' };
        }
        if (Number(invoiceData.currentStep || invoiceData.status_id) === 6 || invoiceData.isDelivered || invoiceData.status === 'delivered') {
            return { text: 'تحویل شده', className: 'wm-completed' };
        }
        return {
            text: invoiceData.status?.title || getStatusTitleById?.(invoiceData.currentStep || invoiceData.status_id) || 'تایید فاکتور',
            className: 'wm-processing'
        };
    };
    const watermarkInfo = getStatusWatermark();

    // ۷. فرمت تاریخ شمسی
    const formatFullShamsiDate = (dateVal) => {
        if (!dateVal) return '-';
        const d = new Date(dateVal);
        return isNaN(d.getTime()) ? String(dateVal) : new Intl.DateTimeFormat('fa-IR', {
            year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
        }).format(d);
    };

    // ۸. رندر هوشمند هزینه ارسال و بسته‌بندی (بدون برچسب بسته‌بندی)
    const renderShippingFee = () => {
        if (sendCost === 0) {
            if (totalPackCost > 0) {
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left', gap: '2px' }}>
                        <div>
                            <span style={{ fontWeight: '700' }}>{toPersianDigits(formatPrice(totalPackCost))} تومان</span>
                        </div>
                        <div style={{ color: '#dc2626', fontWeight: '700', fontSize: '0.92em', marginTop: '1px' }}>
                            + پس‌کرایه (پرداخت در محل)
                        </div>
                    </div>
                );
            }
            return <span style={{ color: '#dc2626', fontWeight: '700' }}>پس‌کرایه (پرداخت در محل)</span>;
        }

        return (
            <span>
                <strong style={{ fontWeight: '700' }}>{toPersianDigits(formatPrice(summary.totalShippingAndPack))}</strong> تومان
            </span>
        );
    };

    const transactions = Array.isArray(invoiceData.finance_trnsactions || invoiceData.transactions)
        ? (invoiceData.finance_trnsactions || invoiceData.transactions)
        : [];

    return (
        <div id="receipt-content" ref={contentRef} className="receipt-paper">
            <div className={`watermark ${watermarkInfo.className}`}>{watermarkInfo.text}</div>

            <div className="receipt-content-layer">
                {/* هدر فاکتور */}
                <div className="receipt-header">
                    <h2 className="shop-name">{seller?.name || 'صورتحساب کالا و خدمات'}</h2>
                    <div className="receipt-sub-title">صورتحساب فروش کالا و خدمات</div>
                    <div className="receipt-meta">
                        <div>شماره: <span className="bold">{toPersianDigits(invoiceData.factorNumber || invoiceData.id || '-')}</span></div>
                        <div>
                            {invoiceData.date ? toPersianDigits(moment(invoiceData.date).format('jYYYY/jMM/jDD')) : '-'}
                            <span style={{ margin: '0 4px' }}>|</span>
                            {invoiceData.date ? toPersianDigits(moment(invoiceData.date).format('HH:mm')) : '-'}
                        </div>
                    </div>
                </div>

                <div className="dashed-line"></div>

                {/* مشخصات خریدار */}
                <div className="receipt-section">
                    <div className="info-row">
                        <span>خریدار:</span>
                        <span className="bold">{buyer?.name} {buyer?.family || ''}</span>
                    </div>
                    {(buyer?.address || invoiceData.address_buyer_archive || invoiceData.address) && (
                        <div className="info-row" style={{ marginTop: '3px', alignItems: 'flex-start' }}>
                            <span style={{ flexShrink: 0 }}>نشانی:</span>
                            <span className="bold text-right" style={{ marginRight: '6px' }}>
                                {buyer?.address || invoiceData.address_buyer_archive || invoiceData.address}
                            </span>
                        </div>
                    )}
                    {(buyer?.postCode || invoiceData.post_code_buyer_archive || invoiceData.post_code) && (
                        <div className="info-row" style={{ marginTop: '3px' }}>
                            <span>کد پستی:</span>
                            <span className="bold">
                                {toPersianDigits(buyer?.postCode || invoiceData.post_code_buyer_archive || invoiceData.post_code)}
                            </span>
                        </div>
                    )}
                    {invoiceData.trackingCodePost && (
                        <div className="info-row" style={{ marginTop: '4px' }}>
                            <span>رهگیری پست:</span>
                            <span className="bold tracking-code" dir="ltr">{toPersianDigits(invoiceData.trackingCodePost)}</span>
                        </div>
                    )}
                </div>

                <div className="dashed-line"></div>

                {/* جدول اقلام */}
                {items.length > 0 ? (
                    <div className="receipt-table">
                        <div className="table-header">
                            <div className="col-name">شرح کالا</div>
                            <div className="col-qty">تعداد</div>
                            <div className="col-price">قیمت پایه</div>
                            <div className="col-disc">تخفیف</div>
                            <div className="col-total">مبلغ کل</div>
                        </div>
                        {items.map((item, index) => {
                            const origPrice = toNumber(item.price || item.originalPrice || item.ourPrice);
                            const count = toNumber(item.amount || item.count, 1);
                            const discountPerItem = toNumber(item.discount_price);
                            const total = toNumber(item.total || item.totalPrice || (origPrice * count - discountPerItem));

                            return (
                                <div key={item.id || index} className="table-row">
                                    <div className="col-name" title={item.commodityName || item.name}>
                                        {toPersianDigits(item.commodityName || item.name || 'نامشخص')}
                                    </div>
                                    <div className="col-qty">{toPersianDigits(count)}</div>
                                    <div className="col-price">{toPersianDigits(formatPrice(origPrice))}</div>
                                    <div className="col-disc">
                                        {discountPerItem > 0 ? toPersianDigits(formatPrice(discountPerItem)) : '-'}
                                    </div>
                                    <div className="col-total bold">{toPersianDigits(formatPrice(total))}</div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="empty-items">اقلام فاکتور یافت نشد</div>
                )}

                <div className="dashed-line"></div>

                {/* خلاصه حساب */}
                <div className="receipt-summary">
                    <div className="summary-row">
                        <span>مجموع قیمت مصرف‌کننده:</span>
                        <span>{toPersianDigits(formatPrice(summary.totalConsumer))} تومان</span>
                    </div>
                    {summary.totalProfit > 0 && (
                        <div className="summary-row profit-box">
                            <span>سود شما از این خرید:</span>
                            <span className="bold">{toPersianDigits(formatPrice(summary.totalProfit))} تومان</span>
                        </div>
                    )}
                    {summary.totalVat > 0 && (
                        <div className="summary-row">
                            <span>مالیات بر ارزش افزوده:</span>
                            <span>{toPersianDigits(formatPrice(summary.totalVat))} تومان</span>
                        </div>
                    )}

                    <div className="summary-row" style={{ alignItems: 'flex-start' }}>
                        <span style={{ paddingTop: '2px' }}>هزینه بسته‌بندی و ارسال:</span>
                        <div>{renderShippingFee()}</div>
                    </div>

                    <div className="summary-row total-pay">
                        <span>مبلغ کل پرداختی:</span>
                        <span>{toPersianDigits(formatPrice(summary.finalPayment))} تومان</span>
                    </div>
                </div>

                {/* اطلاعات پرداخت و تراکنش */}
                {transactions.length > 0 && (
                    <>
                        <div className="dashed-line"></div>
                        <div className="payment-section">
                            <div className="payment-title">اطلاعات پرداخت</div>
                            {transactions.map((tx, index) => {
                                const isCardToCard = String(tx?.transactionType_id) === "1";
                                const refCode = tx?.referenceId || tx?.refId || tx?.trackingCode || tx?.bankTrackingCode;

                                return (
                                    <div key={tx?.id || index} className="payment-card">
                                        <div className="payment-grid">
                                            <div>
                                                <span className="label">مبلغ: </span>
                                                <span className="val success">{toPersianDigits(formatPrice(toNumber(tx?.totalPrice)))} تومان</span>
                                            </div>
                                            <div>
                                                <span className="label">تاریخ: </span>
                                                <span className="val">{toPersianDigits(formatFullShamsiDate(tx?.date))}</span>
                                            </div>

                                            {isCardToCard ? (
                                                <>
                                                    <div className="payment-card-full-row">
                                                        <span className="label">کارت مبدا: </span>
                                                        <span className="val card-number-text" dir="ltr">{formatCardNumber(tx?.sourceCardNumber)}</span>
                                                    </div>
                                                    <div className="payment-card-full-row">
                                                        <span className="label">کارت مقصد: </span>
                                                        <span className="val card-number-text" dir="ltr">{formatCardNumber(tx?.destinationCardNumber)}</span>
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div>
                                                        <span className="label">روش پرداخت: </span>
                                                        <span className="val bold">{tx?.gatewayName || 'پرداخت اینترنتی'}</span>
                                                    </div>
                                                    <div>
                                                        <span className="label">وضعیت: </span>
                                                        <span className="val success">موفق</span>
                                                    </div>
                                                    {refCode && (
                                                        <div className="payment-card-full-row">
                                                            <span className="label">شناسه پیگیری / مرجع: </span>
                                                            <span className="val bold tracking-code" dir="ltr">{toPersianDigits(refCode)}</span>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}

                <div className="receipt-footer">
                    <div>از خرید شما متشکریم</div>
                    <div className="barcode-mock">||||| | |||| ||| |||||| |||| | |||||</div>
                </div>
            </div>
        </div>
    );
}
